import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMobileAuth } from '../MobileAuthProvider';
import { getOfflineActions } from '../utils/offlineStorage';
import { queueOrSend } from '../utils/syncQueue';
import { apiErrorMessage } from '../../utils/apiError';
import { authHeaders } from '../../utils/authHeaders';
import { 
  Thermometer, CheckCircle, AlertTriangle, Clock, FileText
} from 'lucide-react';

const MobileSafety = () => {
  const location = useLocation();
  const { token, isOnline } = useMobileAuth();
  const [activeTab, setActiveTab] = useState('temperature');
  const [temperatureLogs, setTemperatureLogs] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offlineActions, setOfflineActions] = useState([]);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [incidentDraft, setIncidentDraft] = useState({ type: '', description: '', severity: 'medium', location: '' });
  const [feedback, setFeedback] = useState(null);

  const fetchSafetyData = useCallback(async () => {
    try {
      const headers = authHeaders({ token, locationId: localStorage.getItem('current_location_id') });
      
      switch (activeTab) {
        case 'temperature':
          const tempResponse = await fetch('/api/mobile/safety/temperature', {
            headers
          });
          if (tempResponse.ok) setTemperatureLogs(await tempResponse.json());
          break;
          
        case 'checklists':
          const checklistResponse = await fetch('/api/mobile/safety/checklists', {
            headers
          });
          if (checklistResponse.ok) setChecklists(await checklistResponse.json());
          break;
          
        case 'incidents':
          const incidentResponse = await fetch('/api/mobile/safety/incidents', {
            headers
          });
          if (incidentResponse.ok) setIncidents(await incidentResponse.json());
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error fetching safety data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, token]);

  const loadOfflineActions = async () => {
    try {
      const actions = await getOfflineActions();
      setOfflineActions(actions.filter(action => action.queueType === 'safety' || action.type === 'safety'));
    } catch (error) {
      console.error('Error loading offline actions:', error);
    }
  };

  const completeChecklistItem = async (checklistId, itemId, response, notes = '') => {
    setFeedback(null);
    const result = await queueOrSend({
      method: 'POST',
      path: '/api/mobile/safety/checklist/complete',
      body: { checklistId, itemId, response, notes },
      requiresScope: true,
      queueType: 'safety',
    });
    if (result.ok) fetchSafetyData();
    else if (result.queued) {
      setFeedback({ kind: 'queued', msg: 'Checklist response saved on this device and queued to sync.' });
      loadOfflineActions();
    } else setFeedback({ kind: 'error', msg: apiErrorMessage(result.error, 'Could not complete checklist item.') });
  };

  useEffect(() => {
    fetchSafetyData();
    loadOfflineActions();
  }, [fetchSafetyData]);

  useEffect(() => {
    if (location.state?.tab) setActiveTab(location.state.tab);
  }, [location.state]);

  const reportIncident = async (incidentData) => {
    setFeedback(null);
    const result = await queueOrSend({
      method: 'POST',
      path: '/api/mobile/safety/incident/report',
      body: incidentData,
      requiresScope: true,
      queueType: 'safety',
    });
    if (result.ok) {
      setFeedback({ kind: 'ok', msg: 'Incident report recorded.' });
      setIncidentDraft({ type: '', description: '', severity: 'medium', location: '' });
      setShowIncidentModal(false);
      fetchSafetyData();
    }
    else if (result.queued) {
      setFeedback({ kind: 'queued', msg: 'Incident report saved on this device and queued to sync.' });
      loadOfflineActions();
      setIncidentDraft({ type: '', description: '', severity: 'medium', location: '' });
      setShowIncidentModal(false);
    } else setFeedback({ kind: 'error', msg: apiErrorMessage(result.error, 'Could not report incident.') });
  };

  const getTemperatureStatus = (log) => {
    if (!Number.isFinite(log.min_safe_temperature)
      || !Number.isFinite(log.max_safe_temperature)
      || !log.unit
      || log.safe_temperature_unit !== log.unit) {
      return { status: 'not assessed', color: 'gray', icon: Clock };
    }
    if (log.temperature < log.min_safe_temperature || log.temperature > log.max_safe_temperature) {
      return { status: 'unsafe', color: 'red', icon: AlertTriangle };
    }
    return { status: 'safe', color: 'green', icon: CheckCircle };
  };

  const TemperatureTab = () => (
    <div className="space-y-4">
      {/* Temperature values must be entered from an actual measurement. */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-2">Record a measured temperature</h3>
        <p className="text-sm text-gray-600">Enter the equipment and reading you measured. No site equipment limits are configured here.</p>
        <Link to="/mobile/boh/temperature" className="mt-3 inline-flex min-h-11 items-center gap-2 border border-blue-700 px-4 text-sm font-medium text-blue-800">
          <Thermometer aria-hidden="true" className="h-4 w-4" /> Enter reading
        </Link>
      </div>

      {/* Recent Logs */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-3">Recent Logs</h3>
        <div className="space-y-3">
          {temperatureLogs.map(log => {
            const tempStatus = getTemperatureStatus(log);
            const StatusIcon = tempStatus.icon;
            
            return (
              <div key={log.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <StatusIcon className={`w-5 h-5 text-${tempStatus.color}-500`} />
                  <div>
                    <p className="font-medium text-gray-900">{log.equipment_name || log.equipment_id}</p>
                    <p className="text-sm text-gray-500">{new Date(log.timestamp).toLocaleTimeString()}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold text-${tempStatus.color}-600`}>
                    {log.temperature}°{log.unit || 'C'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {tempStatus.status === 'not assessed'
                      ? 'No unit-matched safe range'
                      : `${log.min_safe_temperature}° - ${log.max_safe_temperature}°`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const ChecklistsTab = () => (
    <div className="space-y-4">
      {/* Active Checklists */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-3">Active Checklists</h3>
        <div className="space-y-3">
          {checklists.map(checklist => (
            <div key={checklist.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">{checklist.name}</h4>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  checklist.status === 'completed' ? 'bg-green-100 text-green-800' :
                  checklist.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {checklist.status.replace('_', ' ')}
                </span>
              </div>
              
              <div className="space-y-2">
                {checklist.items.slice(0, 3).map(item => (
                  <div key={item.id} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{item.question}</span>
                    <button
                      onClick={() => completeChecklistItem(checklist.id, item.id, 'yes')}
                      className={`px-3 py-1 rounded text-xs ${
                        item.completed ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {item.completed ? 'Done' : 'Complete'}
                    </button>
                  </div>
                ))}
              </div>
              
              {checklist.items.length > 3 && (
                <button className="text-sm text-blue-600 mt-2">View all items →</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const IncidentsTab = () => (
    <div className="space-y-4">
      {/* Report Incident Button */}
      <button
        type="button"
        onClick={() => setShowIncidentModal(true)}
        className="w-full flex items-center justify-center space-x-2 p-4 bg-red-600 text-white rounded-lg"
      >
        <AlertTriangle className="w-5 h-5" />
        <span>Report Incident</span>
      </button>

      {showIncidentModal && (
        <form
          aria-label="Report safety incident"
          className="space-y-3 border border-gray-300 bg-white p-4"
          onSubmit={(event) => {
            event.preventDefault();
            reportIncident(incidentDraft);
          }}
        >
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Incident details</h3>
            <button type="button" aria-label="Close incident form" onClick={() => setShowIncidentModal(false)} className="p-2">Close</button>
          </div>
          <label className="block text-sm text-gray-700">Incident type
            <input required maxLength="120" value={incidentDraft.type} onChange={(event) => setIncidentDraft((draft) => ({ ...draft, type: event.target.value }))} className="mt-1 block min-h-11 w-full border border-gray-300 px-3" />
          </label>
          <label className="block text-sm text-gray-700">What happened?
            <textarea required maxLength="4000" value={incidentDraft.description} onChange={(event) => setIncidentDraft((draft) => ({ ...draft, description: event.target.value }))} rows={4} className="mt-1 block w-full border border-gray-300 px-3 py-2" />
          </label>
          <label className="block text-sm text-gray-700">Severity
            <select value={incidentDraft.severity} onChange={(event) => setIncidentDraft((draft) => ({ ...draft, severity: event.target.value }))} className="mt-1 block min-h-11 w-full border border-gray-300 px-3">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
            </select>
          </label>
          <label className="block text-sm text-gray-700">Location (optional)
            <input maxLength="160" value={incidentDraft.location} onChange={(event) => setIncidentDraft((draft) => ({ ...draft, location: event.target.value }))} className="mt-1 block min-h-11 w-full border border-gray-300 px-3" />
          </label>
          <button type="submit" className="min-h-11 w-full bg-red-700 px-4 text-sm font-medium text-white">Save incident report</button>
        </form>
      )}

      {/* Recent Incidents */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-3">Recent Incidents</h3>
        <div className="space-y-3">
          {incidents.map(incident => (
            <div key={incident.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{incident.type}</h4>
                  <p className="text-sm text-gray-600 mt-1">{incident.description}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(incident.reportedAt).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  incident.severity === 'critical' ? 'bg-red-100 text-red-800' :
                  incident.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {incident.severity}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3">
          <h1 className="text-xl font-bold text-gray-900">Safety & Compliance</h1>
          <p className="text-sm text-gray-500">Maintain safety standards</p>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200">
          {[
            { id: 'temperature', label: 'Temperature', icon: Thermometer },
            { id: 'checklists', label: 'Checklists', icon: FileText },
            { id: 'incidents', label: 'Incidents', icon: AlertTriangle }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 border-b-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
        <div className="border-b border-gray-200 px-4 py-2">
          <Link
            to="/mobile/safety/inspections"
            className="inline-flex min-h-10 items-center gap-2 text-sm font-medium text-blue-700"
          >
            <FileText aria-hidden="true" className="h-4 w-4" />
            Inspection evidence
          </Link>
        </div>
      </div>

      {/* Offline Sync Status */}
      {!isOnline && offlineActions.length > 0 && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-yellow-800">
              {offlineActions.length} safety logs will sync when online
            </p>
            <button className="text-sm text-yellow-600 underline">
              Sync Now
            </button>
          </div>
        </div>
      )}

      {/* Action Feedback (e.g. a rejected/failed log — never fail silently) */}
      {feedback && (
        <div className={`border-b px-4 py-2 ${feedback.kind === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'}`} role={feedback.kind === 'error' ? 'alert' : 'status'} data-testid={`safety-feedback-${feedback.kind}`}>
          <p className="text-sm">{feedback.msg}</p>
        </div>
      )}

      {/* Tab Content */}
      <div className="px-4 py-4">
        {activeTab === 'temperature' && <TemperatureTab />}
        {activeTab === 'checklists' && <ChecklistsTab />}
        {activeTab === 'incidents' && <IncidentsTab />}
      </div>

    </div>
  );
};

export default MobileSafety;
